import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type SidebarContextValue = {
  expandedNodeIds: Record<string, boolean>;
  setNodeExpanded: (id: string, expanded: boolean) => void;
  resetSidebar: () => void;
};

const SidebarContext = createContext<SidebarContextValue | undefined>(
  undefined
);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [expandedNodeIds, setExpandedNodeIds] = useState<
    Record<string, boolean>
  >({});

  const setNodeExpanded = useCallback((id: string, expanded: boolean) => {
    setExpandedNodeIds(prev => ({ ...prev, [id]: expanded }));
  }, []);

  const resetSidebar = useCallback(() => {
    setExpandedNodeIds({});
  }, []);

  const value = useMemo(
    () => ({ expandedNodeIds, setNodeExpanded, resetSidebar }),
    [expandedNodeIds, setNodeExpanded, resetSidebar]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within SidebarProvider');
  return ctx;
}
